import {
	AssetBreakDownResponse,
	CurveMethod,
	FilterFieldValue,
	GetDeteriorationModelAssetsResultsPaginatedRequests,
	GetDeteriorationModelCurveConfigsPaginatedRequest,
	GetDeteriorationModelFailureGradeChartResponse,
	GetDeteriorationModelGroupsResultsPaginatedRequests,
	GetDeteriorationModelsPaginatedRequest,
	InferByIndustryStandardCurveResponse,
	UpdateFailureGradeRangeRequest,
	UpdateMatrixGradeRequest,
	WeibullDeclineReason,
	WeibullFormulation,
} from '@Types';
import type { WeibullParams } from '@Components/deteriorationModel/utils/weibullFit';
import pipeDeteriorationApi from '@Apis/pipeDeterioration.api';
import { AxiosResponse } from 'axios';
import { Pagination } from '@innovyze/lib-am-common';
import { GridFilterModel } from '@innovyze/stylovyze';

interface CreateDeteriorationModelResponse {
	model_id: string;
}

export const newDeteriorationModel = (request: {
	modelName: string;
	assetGroupSet: string;
	failureDefinition: string | null;
}): Promise<AxiosResponse<CreateDeteriorationModelResponse>> => {
	return pipeDeteriorationApi.post<CreateDeteriorationModelResponse>('deterioration-models', {
		model_name: request.modelName,
		asset_group_set_id: request.assetGroupSet,
		failure_definition_id: request.failureDefinition,
	});
};

interface SingleDeteriorationModelResponse {
	id: string;
	tenant_id: string;
	name: string;
	asset_group_set_id: string;
	failure_definition_id: string | null;
	status: string;
	created_by: string;
	created_at: string;
	trained_by: string;
	trained_at: string | null;
	latest_train_results_id: string | null;
	updated_by: string;
	updated_at: string;
	latest_curve_configs_id: string | null;
	run_by: string | null;
	run_at: string | null;
	latest_run_results_id: string | null;
	system_type?: string;
	asset_type?: string;
	asset_groups_count?: number;
	failure_definition_name?: string;
	published?: boolean;
	failure_grade_config_id?: string;
	weibull_formulation?: string;
}

export interface GetDeteriorationModelsResponse {
	pagination: Pagination;
	data: SingleDeteriorationModelResponse[];
}

export const getDeteriorationModels = (
	request: GetDeteriorationModelsPaginatedRequest,
): Promise<AxiosResponse<GetDeteriorationModelsResponse>> => {
	return pipeDeteriorationApi.get<GetDeteriorationModelsResponse>('deterioration-models', {
		params: {
			page: request.page,
			slice: request.slice,
			sort_key: request.sortKey,
			sort_descending: request.sortDescending,
			active_only: true,
		},
	});
};

interface DuplicateDeteriorationModelResponse {
	model_id: string;
}

export const copyDeteriorationModel = (id: string): Promise<AxiosResponse<DuplicateDeteriorationModelResponse>> => {
	return pipeDeteriorationApi.post<DuplicateDeteriorationModelResponse>(`deterioration-models/${id}/copy`);
};

export const removeDeteriorationModel = (id: string) => {
	return pipeDeteriorationApi.post(`deterioration-models/${id}/delete`);
};

export interface GetDeteriorationModelResponse {
	data: SingleDeteriorationModelResponse;
}

export const getDeteriorationModelById = (id: string) => {
	return pipeDeteriorationApi.get<GetDeteriorationModelResponse>(`deterioration-models/${id}`);
};

export const updateDeteriorationModelName = (request: { modelId: string; newModelName: string }) => {
	return pipeDeteriorationApi.post<{ model_id: string }>(`deterioration-models/${request.modelId}/update-name`, {
		new_model_name: request.newModelName,
	});
};

export const updateFailureDefinition = (request: { modelId: string; newFailureDefinitionId: string }) => {
	return pipeDeteriorationApi.post<{ model_id: string }>(
		`deterioration-models/${request.modelId}/update-failure-definition`,
		{
			new_failure_definition_id: request.newFailureDefinitionId,
		},
	);
};

export const updatePublishedStatus = (request: { modelId: string; published: boolean }) => {
	return pipeDeteriorationApi.post<{ model_id: string }>(
		`deterioration-models/${request.modelId}/update-published-status`,
		{
			published: request.published,
		},
	);
};

const operatorMap: Record<string, string> = {
	contains: 'contains',
	equals: 'equals',
	startsWith: 'starts with',
	endsWith: 'ends with',
	is: 'is',
	not: 'is not',
	'>': '>',
	'>=': '>=',
	'<': '<',
	'<=': '<=',
	'=': '=',
	'!=': '!=',
	isEmpty: 'is empty',
	isNotEmpty: 'is not empty',
	isAnyOf: 'is any of',
};

const curveMethodMap: Record<string, string | null> = {
	failureDefinition: 'failure_definition',
	industryCurve: 'industry_curve',
	customCurve: 'custom_curve',
	oneExpectedLife: 'one_expected_life',
	copiedCurve: 'copied_curve',
	undefined: null,
};

interface SingleCurveConfigResponse {
	_id: string;
	asset_group_id: string;
	asset_group_name: string;
	asset_group_description: string;
	asset_count: number;
	failure_count: number;
	selected_curve_method: {
		method: string;
		custom_data?:
			| {
					age: number;
					survival_probability: number;
			  }[];
		industry_curve_key?: string;
		curve_config_id_reference?: string;
		life_expectancy?: number;
		model?: string;
		weibull_formulation?: string;
	};
}

export interface GetDeteriorationModelCurveConfigsResponse {
	pagination: Pagination;
	data: SingleCurveConfigResponse[];
}

export const listDeteriorationModelCurveConfigs = (
	request: GetDeteriorationModelCurveConfigsPaginatedRequest,
): Promise<AxiosResponse<GetDeteriorationModelCurveConfigsResponse>> => {
	let sortKey = '';
	if (request.sortKey === 'assetGroupName') {
		sortKey = 'asset_group_name';
	} else if (request.sortKey === 'assetGroupDescription') {
		sortKey = 'asset_group_description';
	} else if (request.sortKey === 'selectedCurveMethod') {
		sortKey = 'selected_curve_method';
	} else if (request.sortKey === 'assetCount') {
		sortKey = 'asset_count';
	} else if (request.sortKey === 'failureCount') {
		sortKey = 'failure_count';
	}

	const params: {
		page: number;
		slice: number;
		sort_key: string;
		sort_descending: boolean;
		search_value?: string;
		filter?: Record<string, { operator: string; value: string | (string | null)[] | null }>;
	} = {
		page: request.page,
		slice: request.slice,
		sort_key: sortKey,
		sort_descending: request.sortDescending,
	};

	const filter: Record<string, { operator: string; value: string | (string | null)[] | null }> = {};
	if (request.filter?.assetGroupName) {
		filter.asset_group_name = {
			operator: operatorMap[request.filter.assetGroupName.operator],
			value: request.filter.assetGroupName.value,
		};
	}
	if (request.filter?.assetGroupDescription) {
		filter.asset_group_description = {
			operator: operatorMap[request.filter.assetGroupDescription.operator],
			value: request.filter.assetGroupDescription.value,
		};
	}
	if (request.filter?.selectedCurveMethod) {
		let value: string | (string | null)[] | null = null;
		if (Array.isArray(request.filter.selectedCurveMethod.value)) {
			value = request.filter.selectedCurveMethod.value.map(method => (method == null ? null : curveMethodMap[method]));
		} else if (request.filter.selectedCurveMethod.value) {
			value = curveMethodMap[request.filter.selectedCurveMethod.value];
		}

		filter.selected_curve_method = {
			operator: operatorMap[request.filter.selectedCurveMethod.operator],
			value: value,
		};
	}
	if (request.filter?.assetCount) {
		filter.asset_count = {
			operator: operatorMap[request.filter.assetCount.operator],
			value: request.filter.assetCount.value,
		};
	}
	if (request.filter?.failureCount) {
		filter.failure_count = {
			operator: operatorMap[request.filter.failureCount.operator],
			value: request.filter.failureCount.value,
		};
	}

	if (Object.keys(filter).length > 0) {
		params.filter = filter;
	}

	if (request.searchValue) {
		params.search_value = request.searchValue;
	}

	return pipeDeteriorationApi.post<GetDeteriorationModelCurveConfigsResponse>(
		`deterioration-models/${request.modelId}/curve-configs`,
		params,
	);
};

export const searchDeteriorationModelCurveConfigs = (request: {
	modelId: string;
	searchValue: string;
	selectedCurveMethods?: CurveMethod[];
}) => {
	const params: {
		page: number;
		slice: number;
		sort_key: string;
		sort_descending: boolean;
		search_value: string;
		filter?: Record<string, { operator: string; value: string | string[] | null }>;
	} = {
		page: 1,
		slice: 50, // Limit to 50 results for autocomplete
		sort_key: 'asset_group_name',
		sort_descending: false,
		search_value: request.searchValue,
	};

	// Add filter for selected curve methods if provided
	if (request.selectedCurveMethods && request.selectedCurveMethods.length > 0) {
		params.filter = {
			selected_curve_method: {
				operator: 'is any of',
				value: request.selectedCurveMethods.map(method => {
					if (method === CurveMethod.FAILURE_DEFINITION) {
						return 'failure_definition';
					} else if (method === CurveMethod.INDUSTRY_CURVE) {
						return 'industry_curve';
					} else if (method === CurveMethod.CUSTOM_CURVE) {
						return 'custom_curve';
					} else if (method === CurveMethod.ONE_EXPECTED_LIFE) {
						return 'one_expected_life';
					} else if (method === CurveMethod.COPIED_CURVE) {
						return 'copied_curve';
					} else if (method === CurveMethod.UNDEFINED) {
						return 'undefined';
					}
					return 'undefined';
				}),
			},
		};
	}

	return pipeDeteriorationApi.post<GetDeteriorationModelCurveConfigsResponse>(
		`deterioration-models/${request.modelId}/curve-configs`,
		params,
	);
};

export interface GetIndustryStandardCurvesResponse {
	data: {
		display_name: string;
		key: string;
	}[];
}

export const getIndustryStandardCurves = () => {
	return pipeDeteriorationApi.get<GetIndustryStandardCurvesResponse>('/industry-curves');
};

export interface GetCurveConfigResponse {
	data: SingleCurveConfigResponse;
	asset_group_name: string;
}

export const getCurveConfig = (
	modelId: string,
	curveConfigId: string,
): Promise<AxiosResponse<GetCurveConfigResponse>> => {
	return pipeDeteriorationApi.get<GetCurveConfigResponse>(
		`deterioration-models/${modelId}/curve-configs/${curveConfigId}`,
	);
};

export const inferByIndustryStandardCurve = (request: { curveKeys: string[] | null }) => {
	return pipeDeteriorationApi.post<InferByIndustryStandardCurveResponse>(
		'/industry-curves/infer',
		{
			curve_keys: request.curveKeys,
			ages: Array.from({ length: 100 }, (_, index) => index + 1),
		},
		{
			headers: {
				'Content-Type': 'application/json',
			},
		},
	);
};

/**
 * One formulation's inference result. The endpoint never aliases: an entry is present only when
 * the group has a genuine trained fit for that formulation, so key-presence is proof of a fit and
 * a missing key means "not fitted with this formulation". `params` carries the coefficients the
 * curve was drawn from (2-parameter has no `gamma`).
 */
export interface InferredFormulation {
	inference_results: { age: number; survival_probability: number }[];
	params?: Record<string, number>;
}

export interface InferByFailureDefinitionResponse {
	data: Partial<Record<WeibullFormulation, InferredFormulation>>;
	/**
	 * Why a formulation is missing from `data`, keyed the same way. Absent for groups trained
	 * before I360A-2882; present but without an entry for a formulation that fitted successfully.
	 * `mapFailureDefinitionResponse` normalises the absent case to `{}` — read it through there
	 * rather than off the wire, or the two states have to be handled separately.
	 */
	declines?: Partial<Record<WeibullFormulation, WeibullDeclineReason>>;
}

/**
 * Returns an entry for every Weibull formulation that has a trained fit for the group, so the
 * caller can switch between 2P and 3P locally. `model` is deliberately not sent: naming one
 * formulation would narrow the response and force a refetch on every toggle.
 */
export const inferFailureDefinition = (
	modelId: string,
	curveConfigId: string,
): Promise<AxiosResponse<InferByFailureDefinitionResponse>> => {
	return pipeDeteriorationApi.post<InferByFailureDefinitionResponse>(
		`/deterioration-models/${modelId}/curve-configs/${curveConfigId}/infer/failure_definition`,
		{
			// `ages` is dereferenced unguarded by the API, so it must always be sent.
			ages: Array.from({ length: 100 }, (_, index) => index + 1),
		},
		{
			headers: {
				'Content-Type': 'application/json',
			},
		},
	);
};

export const updateModelCurveConfigs = (request: {
	modelId: string;
	weibullFormulation?: WeibullFormulation;
	curveConfigs: {
		curveConfigId: string;
		selectedCurveMethod: CurveMethod;
		copiedCurve?: string;
		customCurve?: { age: number; survivalProbability: number }[];
		industryCurve?: string;
		oneExpectedLife?: number;
		weibullFormulation?: WeibullFormulation;
	}[];
}) => {
	const curveConfigs = request.curveConfigs.map(config => {
		if (config.selectedCurveMethod === CurveMethod.UNDEFINED) {
			return {
				curve_config_id: config.curveConfigId,
				selected_curve_method: null,
			};
		} else if (config.selectedCurveMethod === CurveMethod.FAILURE_DEFINITION) {
			return {
				curve_config_id: config.curveConfigId,
				selected_curve_method: {
					method: 'failure_definition',
					model: request.weibullFormulation ?? WeibullFormulation.TWO_PARAMETER,
					weibull_formulation: config.weibullFormulation,
				},
			};
		} else if (config.selectedCurveMethod === CurveMethod.INDUSTRY_CURVE) {
			return {
				curve_config_id: config.curveConfigId,
				selected_curve_method: {
					method: 'industry_curve',
					industry_curve_key: config.industryCurve,
				},
			};
		} else if (config.selectedCurveMethod === CurveMethod.CUSTOM_CURVE) {
			return {
				curve_config_id: config.curveConfigId,
				selected_curve_method: {
					method: 'custom_curve',
					custom_data: Array.isArray(config.customCurve)
						? config.customCurve.map(data => ({
								age: data.age,
								survival_probability: data.survivalProbability / 100,
						  }))
						: [],
					weibull_formulation: config.weibullFormulation,
				},
			};
		} else if (config.selectedCurveMethod === CurveMethod.ONE_EXPECTED_LIFE) {
			return {
				curve_config_id: config.curveConfigId,
				selected_curve_method: {
					method: 'one_expected_life',
					life_expectancy: config.oneExpectedLife,
				},
			};
		} else if (config.selectedCurveMethod === CurveMethod.COPIED_CURVE) {
			return {
				curve_config_id: config.curveConfigId,
				selected_curve_method: {
					method: 'copied_curve',
					curve_config_id_reference: config.copiedCurve,
				},
			};
		}
		return undefined;
	});

	return pipeDeteriorationApi.post<{
		message: string;
		model_id: string;
	}>(`deterioration-models/${request.modelId}/update-curve-configs`, { curve_configs: curveConfigs });
};

export const refreshTrainResults = (id: string) => {
	return pipeDeteriorationApi.post(`deterioration-models/${id}/refresh`);
};

export const runModelById = (id: string) => {
	return pipeDeteriorationApi.post(`deterioration-models/${id}/run`);
};

interface SingleDeteriorationModelAssetResultResponse {
	asset_id: string;
	asset_group_id: string;
	asset_group_name: string;
	failure_probability: number | null;
	age: number | null;
	rul: number | null;
	failure_year: number | null;
	historical_failure: string;
	grade: 'negligible' | 'low' | 'medium' | 'high' | 'extreme';
	material: string | null;
	diameter: number | null;
	installation_year: number | null;
}

export interface GetDeteriorationModelAssetsResultsResponse {
	pagination: Pagination;
	data: SingleDeteriorationModelAssetResultResponse[];
}

const assetsSortKeyMap: Record<string, string> = {
	assetId: 'asset_id',
	assetGroupName: 'asset_group_name',
	failureProbability: 'failure_probability',
	rul: 'rul',
	failureYear: 'failure_year',
	age: 'age',
	historicalFailure: 'historical_failure',
	grade: 'grade',
	material: 'material',
	diameter: 'diameter',
	installationYear: 'installation_year',
};

const assetsFilterFieldMap: Array<
	[keyof NonNullable<GetDeteriorationModelAssetsResultsPaginatedRequests['filter']>, string]
> = [
	['assetId', 'asset_id'],
	['assetGroupName', 'asset_group_name'],
	['failureProbability', 'failure_probability'],
	['age', 'age'],
	['rul', 'rul'],
	['failureYear', 'failure_year'],
	['historicalFailure', 'historical_failure'],
	['material', 'material'],
	['diameter', 'diameter'],
	['installationYear', 'installation_year'],
];

const gradeOperatorMap: Record<string, string> = {
	is: 'equals',
	not: 'is not',
	isAnyOf: 'is any of',
	isEmpty: 'is empty',
	isNotEmpty: 'is not empty',
};

export const assetsResultParamsMapping = (
	request: GetDeteriorationModelAssetsResultsPaginatedRequests,
	forExport: boolean,
) => {
	const sortKey = assetsSortKeyMap[request.sortKey] ?? '';

	const params: {
		page: number | string;
		slice: number | string;
		sort_key: string;
		sort_descending: boolean | string;
		search_value?: string;
		filter?: Record<string, { operator: string; value: string | (string | null)[] | null }> | string;
	} = {
		page: forExport ? request.page.toString() : request.page,
		slice: forExport ? request.slice.toString() : request.slice,
		sort_key: sortKey,
		sort_descending: forExport ? request.sortDescending.toString() : request.sortDescending,
	};

	const filter: Record<string, { operator: string; value: string | (string | null)[] | null }> = {};
	if (request.filter) {
		assetsFilterFieldMap.forEach(([fieldName, apiKey]) => {
			const field = request.filter?.[fieldName] as FilterFieldValue | undefined;
			if (field) {
				filter[apiKey] = { operator: operatorMap[field.operator], value: field.value };
			}
		});
		if (request.filter.grade) {
			// Grade uses singleSelect operators ("is"/"not") that need remapping to backend string operators.
			filter.grade = {
				operator: gradeOperatorMap[request.filter.grade.operator] ?? operatorMap[request.filter.grade.operator],
				value: request.filter.grade.value,
			};
		}
	}

	if (Object.keys(filter).length > 0) {
		params.filter = forExport ? JSON.stringify(filter) : filter;
	}

	if (request.searchValue) {
		params.search_value = request.searchValue;
	}

	return params;
};

export const getDeteriorationModelAssetsResults = (
	request: GetDeteriorationModelAssetsResultsPaginatedRequests,
): Promise<AxiosResponse<GetDeteriorationModelAssetsResultsResponse>> => {
	return pipeDeteriorationApi.post(
		`deterioration-models/${request.modelId}/run/${request.runId}/assets`,
		assetsResultParamsMapping(request, false),
	);
};

interface SingleDeteriorationModelGroupResultResponse {
	_id: string;
	asset_group_id: string;
	asset_group_name: string;
	asset_group_description: string;
	asset_count: number | null;
	failure_count: number | null;
	deterioration_method: string;
	threshold_age: number | null;
	deterioration_params?: {
		model: string;
		fallback?: boolean;
		actual_formulation?: string;
		// Why the requested formulation was unavailable, when the backend recorded a reason.
		// Absent on runs from before I360A-2882.
		fallback_reason?: WeibullDeclineReason;
		// The coefficients the run's curve was produced from. Already returned by the API; declared
		// here so the results tab can show what a curve was fitted with, the way the configuration
		// preview does. See I360A-2863 — an unusable curve is only explicable from its params.
		params?: WeibullParams;
	};
}

export interface GetDeteriorationModelGroupsResultsResponse {
	pagination: Pagination;
	data: SingleDeteriorationModelGroupResultResponse[];
}

const GROUPS_SORT_KEY_MAP: Record<string, string> = {
	assetGroupName: 'asset_group_name',
	assetGroupDescription: 'asset_group_description',
	assetCount: 'asset_count',
	failureCount: 'failure_count',
	deteriorationMethod: 'deterioration_method',
	thresholdAge: 'threshold_age',
};

const mapDeteriorationMethodFilter = (
	filterValue: FilterFieldValue,
): { operator: string; value: string | (string | null)[] | null } => {
	let value: string | (string | null)[] | null = null;
	if (Array.isArray(filterValue.value)) {
		value = filterValue.value.map(method => (method == null ? null : curveMethodMap[method]));
	} else if (filterValue.value) {
		value = curveMethodMap[filterValue.value];
	}
	return { operator: operatorMap[filterValue.operator], value };
};

export const groupsResultParamsMapping = (
	request: GetDeteriorationModelGroupsResultsPaginatedRequests,
	forExport: boolean,
) => {
	const sortKey = GROUPS_SORT_KEY_MAP[request.sortKey] ?? '';

	const params: {
		page: number | string;
		slice: number | string;
		sort_key: string;
		sort_descending: boolean | string;
		search_value?: string;
		filter?: Record<string, { operator: string; value: string | (string | null)[] | null }> | string;
	} = {
		page: forExport ? request.page.toString() : request.page,
		slice: forExport ? request.slice.toString() : request.slice,
		sort_key: sortKey,
		sort_descending: forExport ? request.sortDescending.toString() : request.sortDescending,
	};

	const filter: Record<string, { operator: string; value: string | (string | null)[] | null }> = {};
	if (request.filter?.assetGroupName) {
		filter.asset_group_name = {
			operator: operatorMap[request.filter.assetGroupName.operator],
			value: request.filter.assetGroupName.value,
		};
	}
	if (request.filter?.assetGroupDescription) {
		filter.asset_group_description = {
			operator: operatorMap[request.filter.assetGroupDescription.operator],
			value: request.filter.assetGroupDescription.value,
		};
	}
	if (request.filter?.assetCount) {
		filter.asset_count = {
			operator: operatorMap[request.filter.assetCount.operator],
			value: request.filter.assetCount.value,
		};
	}
	if (request.filter?.failureCount) {
		filter.failure_count = {
			operator: operatorMap[request.filter.failureCount.operator],
			value: request.filter.failureCount.value,
		};
	}
	if (request.filter?.thresholdAge) {
		filter.threshold_age = {
			operator: operatorMap[request.filter.thresholdAge.operator],
			value: request.filter.thresholdAge.value,
		};
	}
	if (request.filter?.deteriorationMethod) {
		filter.selected_curve_method = mapDeteriorationMethodFilter(request.filter.deteriorationMethod);
	}

	if (Object.keys(filter).length > 0) {
		if (forExport) {
			params.filter = JSON.stringify(filter);
		} else {
			params.filter = filter;
		}
	}

	if (request.searchValue) {
		params.search_value = request.searchValue;
	}

	return params;
};

export const getDeteriorationModelGroupsResults = (
	request: GetDeteriorationModelGroupsResultsPaginatedRequests,
): Promise<AxiosResponse<GetDeteriorationModelGroupsResultsResponse>> => {
	return pipeDeteriorationApi.post(
		`deterioration-models/${request.modelId}/run/${request.runId}/groups`,
		groupsResultParamsMapping(request, false),
	);
};

// Fetches the latest run's group results and reports whether any 3-parameter selection ran as 2-parameter.
// Used by the Configuration card, which otherwise has no run-results data. Slice is large to cover all groups
// in a single request (asset-group counts per model are bounded).
export const hasAnyGroupWeibullFallback = async (modelId: string, runId: string): Promise<boolean> => {
	const request: GetDeteriorationModelGroupsResultsPaginatedRequests = {
		modelId,
		runId,
		page: 1,
		slice: 1000,
		sortKey: 'assetGroupName',
		sortDescending: true,
		searchValue: null,
		filter: null,
		filterModel: { items: [] } as GridFilterModel,
	};
	const response = await getDeteriorationModelGroupsResults(request);
	// Guard the "counts are bounded" assumption: if a run ever exceeds one page, we only inspect
	// the first page and could return a false negative. Warn rather than silently under-report.
	if (response.data.pagination.total > response.data.data.length) {
		console.warn(
			`hasAnyGroupWeibullFallback: run ${runId} has ${response.data.pagination.total} groups but only ` +
				`${response.data.data.length} were inspected; fallback signal may be incomplete.`,
		);
	}
	return response.data.data.some(result => result.deterioration_params?.fallback === true);
};

interface GetDeteriorationModelGroupsResultsChartResponse {
	data: {
		inference_results: {
			age: number;
			failure_probability: number;
		}[];
	};
}

export const getDeteriorationModelGroupsResultsChart = (
	modelId: string,
	runId: string,
	groupId: string,
): Promise<AxiosResponse<GetDeteriorationModelGroupsResultsChartResponse>> => {
	return pipeDeteriorationApi.post(`deterioration-models/${modelId}/run/${runId}/groups/infer`, {
		group_result_id: groupId,
	});
};

export const getDeteriorationModelFailureGradeMatrix = (
	modelId: string | undefined,
): Promise<AxiosResponse<GetDeteriorationModelFailureGradeChartResponse>> => {
	return pipeDeteriorationApi.get(`deterioration-models/${modelId}/failure-grade`);
};

export const getDeteriorationModelAssetBreakdownChart = (
	modelId: string | undefined,
): Promise<AxiosResponse<AssetBreakDownResponse>> => {
	return pipeDeteriorationApi.get(`deterioration-models/${modelId}/failure-grade/asset-breakdowns`);
};

export const updateFailureGradeRange = (
	request: UpdateFailureGradeRangeRequest,
): Promise<AxiosResponse<GetDeteriorationModelFailureGradeChartResponse>> => {
	return pipeDeteriorationApi.post(`deterioration-models/${request.modelId}/failure-grade`, {
		ruls: request.ruls,
		failure_probabilities: request.failure_probabilities,
		type: request.type,
	});
};

export const updateFailureMatrixGrade = (
	request: UpdateMatrixGradeRequest,
): Promise<AxiosResponse<GetDeteriorationModelFailureGradeChartResponse>> => {
	return pipeDeteriorationApi.post(`deterioration-models/${request.modelId}/failure-grade`, {
		new_grade: request.new_grade,
		old_grade: request.old_grade,
		i: request.i,
		j: request.j,
		type: request.type,
	});
};

export const getGlobalUndefinedCount = (
	modelId: string,
): Promise<AxiosResponse<GetDeteriorationModelCurveConfigsResponse>> => {
	const params: {
		page: number;
		slice: number;
		sort_key: string;
		sort_descending: boolean;
		search_value?: string;
		filter?: Record<string, { operator: string; value: string | (string | null)[] | null }>;
	} = {
		page: 1,
		slice: 1,
		sort_key: 'asset_group_name',
		sort_descending: false,
	};

	params.filter = {
		selected_curve_method: {
			operator: 'is',
			value: null,
		},
	};

	return pipeDeteriorationApi.post<GetDeteriorationModelCurveConfigsResponse>(
		`deterioration-models/${modelId}/curve-configs`,
		params,
	);
};

export const cancelModelRun = (id: string) => {
	return pipeDeteriorationApi.post(`deterioration-models/${id}/cancel-run`);
};
